import React from 'react';
import styled from 'styled-components';
import { Orientation, WriteDirection, Platform, DeviceType, Lightbox } from 'sofa-reborn-react';
import { SkapaTheme } from '@inter-ikea-kompis/themes';

export const StyledHeader = styled.div<{
	deviceType: DeviceType;
	orientation: Orientation;
	writeDirection: WriteDirection;
	platform: Platform;
	addMarginTop: boolean;
}>`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: ${(props) =>
		props.deviceType === 'mobile' &&
		props.orientation === 'landscape' &&
		props.writeDirection === 'rtl'
			? 'flex-end'
			: props.platform === 'kiosk'
			? 'space-between'
			: 'flex-start'};
	margin-top: ${(props) =>
		props.addMarginTop ? (props.deviceType === 'mobile' ? '8px' : '16px') : '0'};

	[data-element-name='kompis-hyperlink'] {
		display: inline-flex;
		padding: ${(props) => (props.platform === 'kiosk' ? '32px' : '8px')};
	}
`;

export const StyledLanguageSelector = styled.div<{
	writeDirection: WriteDirection;
}>`
	display: inline-block;
	margin-right: ${(props) => (props.writeDirection === 'ltr' ? '32px' : '0')};
	margin-left: ${(props) => (props.writeDirection === 'rtl' ? '32px' : '0')};
`;

export const StyledUpptackaButton = styled.div<{
	writeDirection: WriteDirection;
	isPIG: boolean;
	addBorder: boolean;
}>`
	display: flex;
	align-items: center;
	padding-right: ${(props) =>
		props.writeDirection === 'ltr' && props.isPIG
			? '32px'
			: props.writeDirection === 'rtl' && !props.isPIG
			? '20px'
			: '0'};
	padding-left: ${(props) =>
		props.writeDirection === 'rtl' && props.isPIG
			? '32px'
			: props.writeDirection === 'ltr' && !props.isPIG
			? '20px'
			: '0'};
	border-right: ${(props) =>
		props.addBorder && props.writeDirection === 'ltr' ? '1px solid #dfdfdf' : 'none'};
	border-left: ${(props) =>
		props.addBorder && props.writeDirection === 'rtl' ? '1px solid #dfdfdf' : 'none'};
`;

export const NavigationWrapper = styled.div<{
	isKiosk: boolean;
}>`
	display: flex;
	flex-grow: 1;

	.switch {
		display: flex;
		align-items: center;
		margin: ${(props) => (props.isKiosk ? '0 32px' : '0 16px')};

		*,
		*::before,
		*::after {
			box-sizing: border-box;
		}
	}
`;

export const ButtonContainer = styled.div`
	display: flex;
`;

function getShowActionButtonsLinkStyling(
	orientation: Orientation,
	writeDirection: WriteDirection,
	toolsBottom: number
): string {
	if (orientation === 'portrait') {
		return writeDirection === 'rtl'
			? `
					left: 0;
					bottom: ${toolsBottom}px;
			  `
			: `
					right: 0;
					bottom: ${toolsBottom}px;
			  `;
	} else {
		return writeDirection === 'rtl'
			? `
					left: 0;
					top: 0;
			  `
			: `
					right: 115px;
					top: 0;
			  `;
	}
}

function getBorderStyle(writeDirection: WriteDirection): string {
	return writeDirection === 'rtl'
		? `
				border-left: 1px solid ${SkapaTheme.color.border.light};
		  `
		: `
				border-right: 1px solid ${SkapaTheme.color.border.light};
		  `;
}

function getPaddingStyle(writeDirection: WriteDirection): string {
	return writeDirection === 'rtl'
		? `
				padding-left: 32px;
		  `
		: `
				padding-right: 32px;
		  `;
}

export const StyledShowActionButtonsLink = styled.div<{
	orientation: Orientation;
	writeDirection: WriteDirection;
	toolsBottom: number;
}>`
	position: absolute;
	${(props) =>
		getShowActionButtonsLinkStyling(props.orientation, props.writeDirection, props.toolsBottom)}
`;

export const BackButtonWrapper = styled.div<{
	writeDirection: WriteDirection;
	platform: Platform;
	deviceType: DeviceType;
	isUpptacka: boolean;
}>`
	padding-right: ${(props) =>
		props.isUpptacka && props.writeDirection === 'ltr'
			? `${SkapaTheme.spacing.space200}px`
			: props.writeDirection === 'rtl' && props.deviceType === 'desktop' && props.platform === 'web'
			? `${SkapaTheme.spacing.space100}px`
			: 0};
	padding-left: ${(props) =>
		props.isUpptacka && props.writeDirection === 'rtl' ? `${SkapaTheme.spacing.space200}px` : 0};
	width: ${(props) =>
		props.platform === 'kiosk'
			? props.isUpptacka
				? 'auto'
				: '116px'
			: props.deviceType === 'mobile'
			? '52px'
			: '68px'};
	display: flex;
	align-items: center;
	${(props) => props.platform === 'kiosk' && getBorderStyle(props.writeDirection)}
`;

export const GalleryButtonWrapper = styled.div<{
	writeDirection: WriteDirection;
	platform: Platform;
	showIpexGalleryButton: boolean;
}>`
	display: flex;
	align-items: center;
	color: ${SkapaTheme.color.icon.default};
	${(props) => props.platform === 'kiosk' && getBorderStyle(props.writeDirection)}
	${(props) => !props.showIpexGalleryButton && getPaddingStyle(props.writeDirection)}
`;

export const AddFakeDataButton = styled.div`
	margin-right: 15px;
	margin-left: 15px;
`;

export const StyledHeaderContainer = styled.div<{
	noBack: boolean | null;
}>`
	${(props): string => {
		if (props.noBack) {
			return `
				 {
				}
			`;
		} else {
			return `
				display: flex;
				justify-content: flex-start;
			`;
		}
	}}
`;

export function getIconButtonStyle(
	platform: Platform,
	writeDirection: WriteDirection,
	deviceType: DeviceType,
	orientation: Orientation
): React.CSSProperties {
	if (platform === 'kiosk') {
		return writeDirection === 'rtl' ? { marginRight: '32px' } : { marginLeft: '32px' };
	} else {
		return writeDirection === 'rtl' && deviceType === 'mobile' && orientation === 'landscape'
			? { marginRight: '4px' }
			: writeDirection === 'rtl' && deviceType !== 'mobile'
			? { marginRight: '16px' }
			: deviceType !== 'mobile'
			? { marginLeft: '16px' }
			: { margin: '0' };
	}
}

export function getIconStyle(
	writeDirection: WriteDirection,
	isSeriesGalleryButton: boolean
): React.CSSProperties {
	return isSeriesGalleryButton
		? {
				marginRight: writeDirection === 'ltr' ? '24px' : 0,
				marginLeft: writeDirection === 'ltr' ? 0 : '24px',
		  }
		: {
				marginRight: writeDirection === 'ltr' ? '16px' : 0,
				marginLeft: writeDirection === 'ltr' ? 0 : '16px',
		  };
}

export function getHeaderTextStyle(
	writeDirection: WriteDirection,
	currentLightbox: Lightbox,
	showIpexGalleryButton: boolean,
	isInUpptackaPlatform: boolean
): React.CSSProperties {
	return {
		marginLeft:
			currentLightbox === 'product-intro-gallery' && !showIpexGalleryButton && !isInUpptackaPlatform
				? '0'
				: writeDirection === 'ltr'
				? showIpexGalleryButton
					? '16px'
					: '32px'
				: '0px',
		marginRight:
			currentLightbox === 'product-intro-gallery' && !showIpexGalleryButton && !isInUpptackaPlatform
				? '0'
				: writeDirection === 'rtl'
				? showIpexGalleryButton
					? '16px'
					: '32px'
				: '0px',
		display: 'flex',
		alignItems: 'center',
	};
}
